import { ComposableElement } from '../ComposableElement.mjs'

/** 
 * Tabs Web Component
 * 
 * This component serves as the main container for a tabbed interface, managing the state and interaction between the tab list and the tab panels. 
 * It listens for custom events from the `ba-tablist` subcomponent to switch between tabs and ensures that the correct tab panel is displayed based on user interaction.
 * 
 * This component also demonstrates how to use JavaScript to create and manage a complex Web Component with multiple subcomponents, how to handle custom events for interactivity, and how to structure the component for maximum flexibility and reusability.
 */
export class Tabs extends ComposableElement {
    constructor() {
        super();

        this._changeTabFunc = this._changeTab.bind(this);
    }

    /**
     * Does initial tab setup and adds event listeners for tab interaction.
     * 
     * `connectedCallback` is a lifecycle method in web components that runs when the custom element is inserted into the document's Document Object Model (DOM). 
     * It can be invoked multiple times if the element is removed and then re-inserted into the DOM.
     * 
     * Timing: It is called after the element's constructor() but before the element's children are necessarily connected or fully rendered.
     * Purpose: It is the ideal place to set up tasks that should only occur when the element is actually present in the live document. Common uses include:
     */
    connectedCallback() {
        // Attach the internals to the component to allow for the use of a Declarative Shadow Root
        const internals = this.attachInternals();
        
        // check for a Declarative Shadow Root:
        this.shadow = this.shadowRoot;
        if (!this.shadow) {
            // there wasn't one. create a new Shadow Root:
            this.shadow = this.attachShadow({ mode: 'open' });

            setTimeout(() => {
                // Recreate the template using the shadow DOM that is only available through JavaScript
                this.createTemplateInJS(this.shadow);
            }, 0);
        }

        setTimeout(() => {
            this.addEventListener('ba-tab-change', this._changeTabFunc);
        }, 0);
    }

    /** Cleanup when the element is removed from the DOM */
    disconnectedCallback() {
        this.removeEventListener('ba-tab-change', this._changeTabFunc);
    }

    /**
     * Get the currently active tab.
     * 
     * useful for external interactions with the component.
     * Note, there is some ambiguity in what the "active tab" means.
     * Is it the button element ("tab") itself or the panel?
     * 
     * For that reason we return an object that can be summarized using the following table:
     * 
     * | Key     | Description               | Rational                                                                                                       |
     * | ------- | ------------------------- | -------------------------------------------------------------------------------------------------------------- |
     * | `id`    | The ID of the the panel   | If the question is "what is the ID of the active tab?" Your not looking for the ID of the button element       |
     * | `tab`   | The button ("tab") itself | Provides access to the underlying button itself if/when needed while not over-complicating other things        |
     * | `panel` | The panel (tab contents)  | Provides access to the underlying panel (tab contents) if/when needed while not over-complicating other things |
     * 
     * @return {{ id: string, tab: HTMLButtonElement, panel: HTMLDivElement }} The currently active tab element
     */
    getActiveTab() {
        const tablist = this.shadow.querySelector('ba-tablist');
        const tabPanels = this.shadow.querySelector('ba-tab-panels');

        if (!tablist || !tabPanels) {
            console.error('Tablist or Tab Panels subcomponent not found. Cannot get active tab.');
            return null;
        }

        const tab = tablist.getActiveTab();
        const id = tabPanels.getActiveTab().id;
        const panel = tabPanels.getActiveTab().panel;
        
        return { id, tab, panel };
    }

    /**
     * Switch the active tab to the selected tab and show the corresponding tab panel while hiding the others.
     * 
     * Note, this is meant to be available both internally to the class but also externally (if JavaScript ever needed to programmatically switch the active tab, they could call this method directly) so it is not prefixed with an underscore like the other internal helper methods.
     * 
     * @param {HTMLElement} selectedTab The tab element to activate
     */
    switchTab(selectedTab) {
        this.shadow.querySelector('ba-tablist').switchTab(selectedTab);
    }

    /**
     * Internal change event handler for when a tab is changed.
     * It prevents the default behavior and then calls the `switchPanel` method to switch the active tab to the clicked/selected tab.
     * 
     * @param {Event} e The change tab event triggered by the ba-tablist element. The event listener for this is added in the `connectedCallback` method. 
     */
    async _changeTab(e) {
        // Prevent the default behavior
        e.preventDefault();

        const targetId = e.detail.targetId;

        if(targetId === this.shadow.querySelector('ba-tablist').getActiveTab().id) {
            // The clicked tab is already active, so we don't need to do anything
            return;
        }

        const tabPanels = this.shadow.querySelector('ba-tab-panels');
        if (typeof tabPanels.switchPanel === 'function') {
            tabPanels.switchPanel(targetId);
        }
    }

    /**
     * Recreate the template in the shadow DOM through JavaScript instead of relying on the `shadowrootmode` attribute
     * 
     * @param {ShadowRoot} shadow The shadow DOM to attach the template to
     */
    createTemplateInJS(shadow) {
        const config = this.initializeComponent('tabs', shadow);
        
        const tabsContainerDiv = document.createElement('div');
        tabsContainerDiv.classList.add('tabs-container');

        // Dynamically generate the arrays of slot names based on the config
        const tablistSlotNames = config.tabsMeta.map((_, index) => `tab-${index}`);
        const panelSlotNames = config.tabsMeta.map((_, index) => `tab-content-${index}`);

        // Create the Tablist Subcomponent
        const tablistElem = this.createSubComponent('tablist', config, tablistSlotNames);
        tabsContainerDiv.appendChild(tablistElem);

        // Create the Tab Panels Subcomponent
        const tabPanelsElem = this.createSubComponent('tab-panels', config, panelSlotNames);
        tabsContainerDiv.appendChild(tabPanelsElem);

        shadow.appendChild(tabsContainerDiv);
    }
}

document.addEventListener('DOMContentLoaded', () => {
    if (!customElements.get('ba-tabs')) {
        customElements.define('ba-tabs', Tabs);
    }
});